import { fetchGeoData, type Predictions } from '@sg/garnish';

import { getEnvVars } from '@order/utils';

import { type LocationSearchMachineEvents } from '../../LocationSearch.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a place data based on the provided list of predictions.
 */
export async function selectPrediction(params: Params) {
  const { predictions, event } = params;
  const { placeId } = event;

  if (!placeId) return;

  const selectedPrediction = predictions.find(({ place_id }) => place_id === placeId); // prettier-ignore

  if (!selectedPrediction) return;

  const { description } = selectedPrediction;

  const geoData = await safelyGetGeoData(placeId, GOOGLE_API_KEY as string);
  const result = geoData?.results[0];

  const { lat, lng } = result?.geometry?.location ?? {};
  const types = result?.types ?? [];

  const bounds = extractBounds(
    result?.geometry?.bounds as GeometryBounds | undefined,
  );

  return { description, lat, lng, types, bounds };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extracts bounds from Google Places API geometry bounds if available.
 */
function extractBounds(geometryBounds: GeometryBounds) {
  const soutwestBounds = geometryBounds?.southwest;
  const northeastBounds = geometryBounds?.northeast;

  const hasValidSouthwest =
    typeof soutwestBounds?.lat === 'number' &&
    typeof soutwestBounds?.lng === 'number';
  const hasValidNortheast =
    typeof northeastBounds?.lat === 'number' &&
    typeof northeastBounds?.lng === 'number';

  if (!hasValidSouthwest || !hasValidNortheast) {
    return;
  }

  return {
    southwest: { lat: soutwestBounds.lat, lng: soutwestBounds.lng },
    northeast: { lat: northeastBounds.lat, lng: northeastBounds.lng },
  };
}

async function safelyGetGeoData(placeId: string, googlePlacesApiKey: string) {
  try {
    return await fetchGeoData({ placeId, apiKey: googlePlacesApiKey });
  } catch {
    return null;
  }
}

const { GOOGLE_API_KEY } = getEnvVars();

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  predictions: Predictions;
  event: SelectPredictionEvent;
};

type SelectPredictionEvent = Extract<
  LocationSearchMachineEvents,
  { type: 'SELECT_PREDICTION' }
>;

type GeometryBounds =
  | {
      southwest?: { lat: number; lng: number };
      northeast?: { lat: number; lng: number };
    }
  | undefined;
